import { Delete } from '@mui/icons-material'
import { Box, Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import {
  useAddFederationDomainMutation,
  useDeleteFederationDomainMutation,
  useGetFederationProvidersQuery,
  useGetVerifiedDomainsQuery,
  useLinkFederationDomainMutation,
} from '../../../app/redux-fetch/apiQuery'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import { VerifiedDomainStatus } from '../VerifiedDomains/types'
import DomainBadge from './DomainBadge'
import messages from './messages'
import { FederationProviderListItem } from './types'

interface EditFederationProviderModalProps {
  provider: FederationProviderListItem
}

// E12 F12-5 (WI 635) + F12-6 relink (WI 638) — manage a provider's domains: add a verified domain, remove
// one (the server guards the last domain → 409), or link a grandfathered unlinked domain to a now-verified
// domain (clears the caution badge). The provider is re-read live from the list cache so the panel reflects
// each mutation without reopening.
const EditFederationProviderModal = ({ provider }: EditFederationProviderModalProps) => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const { tenantId, customerId } = loggedInUser

  const { data: providers } = useGetFederationProvidersQuery()
  const live = providers?.find((p) => p.id === provider.id) ?? provider

  const { data: verifiedDomains } = useGetVerifiedDomainsQuery(
    { tenantId, customerId },
    { skip: !tenantId },
  )
  const [addDomain] = useAddFederationDomainMutation()
  const [deleteDomain] = useDeleteFederationDomainMutation()
  const [linkDomain] = useLinkFederationDomainMutation()

  const [addSelection, setAddSelection] = useState<number | ''>('')
  // The domain row currently being linked, and the verified-domain chosen for it.
  const [linkingDomainId, setLinkingDomainId] = useState<number | undefined>()
  const [linkSelection, setLinkSelection] = useState<number | ''>('')

  const verified = useMemo(
    () => (verifiedDomains || []).filter((d) => d.status === VerifiedDomainStatus.Verified),
    [verifiedDomains],
  )
  // Verified domains not already on the provider — the Add picker's options.
  const addable = useMemo(() => {
    const present = new Set(live.domains.map((d) => d.domainName))
    return verified.filter((d) => !present.has(d.domainName))
  }, [verified, live.domains])

  const handleAdd = async () => {
    const chosen = verified.find((d) => d.id === addSelection)
    if (!chosen) return
    const res = await addDomain({
      providerId: live.id,
      body: { domainName: chosen.domainName, verifiedDomainId: chosen.id },
    })
    if ('error' in res && res.error) return // the base query already surfaces the error toast
    enqueueSnackbar(formatMessage(messages.domainAdded), { variant: 'success' })
    setAddSelection('')
  }

  const handleRemove = async (domainId: number) => {
    const res = await deleteDomain({ providerId: live.id, domainId })
    if ('error' in res && res.error) return // last-domain 409 (etc.) already toasted by the base query
    enqueueSnackbar(formatMessage(messages.domainRemoved), { variant: 'success' })
  }

  const handleLink = async (domainId: number) => {
    if (!linkSelection) return
    const res = await linkDomain({
      providerId: live.id,
      domainId,
      body: { verifiedDomainId: linkSelection },
    })
    if ('error' in res && res.error) return
    enqueueSnackbar(formatMessage(messages.domainLinked), { variant: 'success' })
    setLinkingDomainId(undefined)
    setLinkSelection('')
  }

  return (
    <Box sx={{ m: '32px' }}>
      <Typography variant='subtitle1' sx={{ mb: 2 }}>
        {formatMessage(messages.editDomainsHeading)}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {live.domains.map((d) => (
          <Box key={d.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <DomainBadge domain={d} />
              </Box>
              {d.verifiedDomainId == null && (
                <Button
                  size='small'
                  onClick={() => {
                    setLinkingDomainId((prev) => (prev === d.id ? undefined : d.id))
                    setLinkSelection('')
                  }}
                >
                  {formatMessage(messages.linkButton)}
                </Button>
              )}
              <IconButton
                color='primary'
                aria-label={formatMessage(messages.remove)}
                onClick={() => handleRemove(d.id)}
              >
                <Delete />
              </IconButton>
            </Box>
            {linkingDomainId === d.id && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <TextField
                  select
                  size='small'
                  fullWidth
                  label={formatMessage(messages.verifiedDomainLabel)}
                  value={linkSelection}
                  onChange={(e) => setLinkSelection(Number(e.target.value))}
                >
                  {/* Re-linking proves ownership of THIS domain, so only offer the verified domain of the
                      same name (both are stored lowercased). Empty ⇒ that domain isn't verified yet. */}
                  {verified
                    .filter((v) => v.domainName === d.domainName)
                    .map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.domainName}
                      </MenuItem>
                    ))}
                </TextField>
                <Button
                  variant='contained'
                  disabled={!linkSelection}
                  onClick={() => handleLink(d.id)}
                  sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                >
                  {formatMessage(messages.linkButton)}
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          select
          size='small'
          fullWidth
          label={formatMessage(messages.verifiedDomainLabel)}
          value={addSelection}
          onChange={(e) => setAddSelection(Number(e.target.value))}
          disabled={addable.length === 0}
          helperText={addable.length === 0 ? formatMessage(messages.noVerifiedDomains) : undefined}
        >
          {addable.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.domainName}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant='contained'
          disabled={!addSelection}
          onClick={handleAdd}
          sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
        >
          {formatMessage(messages.addDomainButton)}
        </Button>
      </Box>
    </Box>
  )
}

export default EditFederationProviderModal
